import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Card from '../common/Card';

const COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#14B8A6', // Teal
  '#F97316', // Orange
];

const BarChartCard = ({ title, data, total, fieldName, onSliceClick }) => {
  // Take top 10 values for bar chart
  const chartData = data.slice(0, 10).map((item, index) => ({
    name: item.value.length > 20 ? item.value.substring(0, 18) + '...' : item.value,
    fullName: item.value,
    count: item.count,
    percentage: item.percentage,
    color: COLORS[index % COLORS.length]
  }));

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="font-semibold text-gray-900 mb-1">{payload[0].payload.fullName}</p>
          <p className="text-sm text-gray-600">
            Count: <span className="font-bold">{payload[0].value}</span>
          </p>
          <p className="text-sm text-gray-600">
            Percentage: <span className="font-bold">{payload[0].payload.percentage.toFixed(1)}%</span>
          </p>
          {onSliceClick && (
            <p className="text-xs text-blue-600 mt-1 italic">Click to view attendees</p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="chart-container shadow-sm border-gray-200" id={`chart-${fieldName}`}>
      <Card.Content className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        
        {chartData.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart 
                data={chartData}
                margin={{ top: 5, right: 20, left: 10, bottom: 80 }}
                onClick={(data) => {
                  if (onSliceClick && data && data.activePayload && data.activePayload.length > 0) {
                    onSliceClick(fieldName, data.activePayload[0].payload.fullName, title);
                  }
                }}
                className={onSliceClick ? "cursor-pointer" : ""}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="name" 
                  angle={-45} 
                  textAnchor="end" 
                  height={80}
                  style={{ fontSize: '11px' }}
                  stroke="#6B7280"
                />
                <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} />
                <Tooltip content={<CustomTooltip />} cursor={{fill: 'rgba(0,0,0,0.05)'}} />
                <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.color} 
                      className={onSliceClick ? "hover:opacity-80 transition-opacity" : ""}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Summary below chart */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Total responses:</span>
                <span className="font-bold text-gray-900">{total}</span>
              </div>
              <div className="flex justify-between items-center text-sm mt-1">
                <span className="text-gray-600">Unique values:</span>
                <span className="font-bold text-gray-900">{data.length}</span>
              </div>
              {data.length > 10 && (
                <p className="text-xs text-gray-500 mt-2">
                  Showing top 10 of {data.length} values
                </p>
              )}
            </div>
          </>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-gray-400">
            No data available
          </div>
        )}
      </Card.Content>
    </Card>
  );
};

export default BarChartCard;
